import React from 'react';
import { useConfigStore } from '@stores/configStore';
import { Database, AlertCircle, CheckCircle } from 'lucide-react';
import type { OracleDatabaseConfig } from '../types/config';

interface OracleDatabaseFormProps {
  onTestConnection?: () => Promise<void>;
  testResult?: { success: boolean; message: string };
}

export const OracleDatabaseForm: React.FC<OracleDatabaseFormProps> = ({ onTestConnection, testResult }) => {
  const { config } = useConfigStore();
  const db = config.database.oracle;

  if (!db) return null;

  const handleUpdate = (updates: Partial<OracleDatabaseConfig>) => {
    const { updateDatabase } = useConfigStore.getState();
    updateDatabase({ oracle: { ...db, ...updates } });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Database className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold">Oracle Database Connection</h3>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
          <input
            type="text"
            value={db.host}
            onChange={(e) => handleUpdate({ host: e.target.value })}
            placeholder="e.g., localhost or 192.168.1.100"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
          <input
            type="number"
            value={db.port}
            onChange={(e) => handleUpdate({ port: parseInt(e.target.value) })}
            placeholder="1521"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Connection Type</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={db.connectionType === 'SID'}
              onChange={() => handleUpdate({ connectionType: 'SID' })}
              className="w-4 h-4"
            />
            <span className="text-sm">SID</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={db.connectionType === 'SERVICE_NAME'}
              onChange={() => handleUpdate({ connectionType: 'SERVICE_NAME' })}
              className="w-4 h-4"
            />
            <span className="text-sm">Service Name</span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {db.connectionType === 'SID' ? 'SID' : 'Service Name'}
        </label>
        <input
          type="text"
          value={db.sidOrServiceName}
          onChange={(e) => handleUpdate({ sidOrServiceName: e.target.value })}
          placeholder={db.connectionType === 'SID' ? 'e.g., ORCL' : 'e.g., orcl.example.com'}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
          <input
            type="text"
            value={db.username}
            onChange={(e) => handleUpdate({ username: e.target.value })}
            placeholder="e.g., apps"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <input
            type="password"
            value={db.password}
            onChange={(e) => handleUpdate({ password: e.target.value })}
            placeholder="••••••••"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pool Size</label>
          <input
            type="number"
            value={db.poolSize}
            onChange={(e) => handleUpdate({ poolSize: parseInt(e.target.value) })}
            min="1"
            max="100"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Connection Timeout (ms)</label>
          <input
            type="number"
            value={db.connectionTimeout}
            onChange={(e) => handleUpdate({ connectionTimeout: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Idle Timeout (ms)</label>
          <input
            type="number"
            value={db.idleTimeout}
            onChange={(e) => handleUpdate({ idleTimeout: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="ssl"
          checked={db.ssl}
          onChange={(e) => handleUpdate({ ssl: e.target.checked })}
          className="w-4 h-4 text-blue-600 rounded"
        />
        <label htmlFor="ssl" className="text-sm font-medium text-gray-700">
          Use SSL Connection
        </label>
      </div>

      {db.ssl && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Wallet Path (Optional)</label>
          <input
            type="text"
            value={db.walletPath || ''}
            onChange={(e) => handleUpdate({ walletPath: e.target.value })}
            placeholder="/path/to/wallet"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">Path to Oracle Wallet for SSL connections</p>
        </div>
      )}

      {onTestConnection && (
        <button
          onClick={onTestConnection}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Test Connection
        </button>
      )}

      {testResult && (
        <div className={`p-4 rounded-lg flex items-start gap-3 ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
          {testResult.success ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={testResult.success ? 'text-green-800' : 'text-red-800'}>{testResult.message}</p>
        </div>
      )}
    </div>
  );
};
